<script setup lang="ts">
import type { PageFeatureProps } from '#ui/components/PageFeature.vue';

import { isDirectSale, isPlatformSaleListingItemEntity, type PlatformSaleItemEntity, type PlatformSaleListingItemEntity } from '~/types/sale.type';

// PROPS
interface Props {
    sale?: PlatformSaleItemEntity | PlatformSaleListingItemEntity;
}
const { sale } = defineProps<Props>();

// COMPOSABLES
const { isMobile } = useResponsive();
const { tt } = useTypedI18n();
const { busDossiers } = useFleequidStats();

// DATA
const open = ref(false);
const offer = ref<number>();
const isSeller = computed(() => {
    if (sale) {
        const { isSeller } = isDirectSale(sale) ? useDirectSaleStatus(toRef(() => sale)) : useAuctionStatus(toRef(() => sale));

        return isSeller.value;
    }

    return false;
});
const assetsCount = computed(() => {
    if (!sale) return 0;

    return isPlatformSaleListingItemEntity(sale) ? sale.assetCount : sale.assets.length;
});
const features = computed<PageFeatureProps[]>(() => [
    {
        title: tt('fees.infoModal.item1.title'),
        description: tt('fees.infoModal.item1.description', { count: busDossiers.value }),
        icon: 'lucide:shield-check',
        ui: { leadingIcon: 'text-success' },
    },
    {
        title: tt('fees.infoModal.item2.title'),
        description: tt('fees.infoModal.item2.description'),
        icon: 'lucide:circle-check-big',
    },
    {
        title: tt('fees.infoModal.item3.title'),
        description: tt('fees.infoModal.item3.description'),
        icon: 'lucide:lock',
        ui: { leadingIcon: 'text-warning' },
    },
    {
        title: tt('fees.infoModal.item4.title'),
        description: tt('fees.infoModal.item4.description'),
        icon: 'lucide:file-check',
        ui: { leadingIcon: 'text-info' },
    },
]);

// WATCHERS
watch(open, value => {
    if (!value) {
        offer.value = undefined;
    }
});
</script>

<template>
    <UModal
        v-model:open="open"
        :fullscreen="isMobile"
        :title="tt('fees.infoModal.title')"
        :ui="{ content: 'max-w-3xl' }"
        v-bind="modalNoDescAriaFixupProps"
    >
        <UButton class="p-0" color="neutral" variant="ghost" size="xs" @click.stop.prevent>
            <UIcon name="lucide:info" />
        </UButton>

        <template #body>
            <div class="space-y-3">
                <p>{{ tt('fees.infoModal.description') }}</p>
                <UPageFeature v-for="(feature, index) in features" v-bind="feature" :key="index" />
                <UCollapsible class="space-y-2">
                    <UButton
                        :ui="{
                            trailingIcon: 'group-data-[state=open]:rotate-180 transition-transform duration-200',
                        }"
                        class="group"
                        size="md"
                        :label="tt('fees.infoModal.more')"
                        color="neutral"
                        variant="link"
                        trailing-icon="i-lucide-chevron-down"
                    />

                    <template #content>
                        <div class="space-y-2">
                            <SaleFeesInfoTable />
                            <p class="text-center text-sm font-bold">{{ tt('fees.infoModal.bottomNotice') }}</p>
                        </div>
                    </template>
                </UCollapsible>
                <UPageCard v-if="sale" :title="tt('fees.infoModal.calculator.title')" class="w-full">
                    <UFormField :label="tt('fees.infoModal.calculator.priceLabel')">
                        <UInput v-model="offer" :placeholder="tt('fees.infoModal.calculator.pricePlaceholder')" type="number" />
                    </UFormField>
                    <SaleFees
                        v-if="offer"
                        wrapper-class="text-left"
                        :is-buyer="!isSeller"
                        :base-amount="offer"
                        :assets-count="assetsCount"
                        :fee-configuration="sale.userMeta?.feeOverride"
                    />
                </UPageCard>
            </div>
        </template>
    </UModal>
</template>
